// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import brandDetailsItemSharedStyles from "@adl/App/SharedStyle/BrandDetailsItemSharedStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { BrandProductLinesStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    showImages = "showImages",
    maxToShow = "maxToShow",
    gridType = "gridType",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showImages]: boolean;
        [fields.maxToShow]: number;
        [fields.gridType]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandDetailsProductLinesStyles {
    titleItem?: GridItemProps;
    title?: TypographyProps;
    container?: InjectableCss;
    productLineContainer?: GridContainerProps;
    productLineItemRow?: GridItemProps;
    productLineItemColumn?: GridItemProps;
    innerProductLineContainer?: ClickablePresentationProps;
    productLineImageItem?: GridItemProps;
    productLineImage?: LazyImageProps;
    /** @deprecated item is no longer used for syntatic correctness */
    productLineNameLinkItem?: GridItemProps;
    productLineNameItem?: GridItemProps;
    productLineNameLink?: LinkProps;
    productLineName?: TypographyProps;
    viewAllButtonItem?: GridItemProps;
    viewAllButton?: ButtonProps;
    /** @deprecated Clickable component is no longer used for syntatic correctness */
    productLineImageClickable?: ClickablePresentationProps;
}

export const productLinesStyles: BrandDetailsProductLinesStyles = {
    title: {
        ...brandDetailsItemSharedStyles.title,
    },
    container: {
        ...brandDetailsItemSharedStyles.container,
    },
    innerProductLineContainer: {
        ...brandDetailsItemSharedStyles.innerContainer,
    },
    productLineContainer: {
        ...brandDetailsItemSharedStyles.categoryContainer,
    },
    productLineItemRow: {
        ...brandDetailsItemSharedStyles.itemRow,
    },
    productLineImage: {
        ...brandDetailsItemSharedStyles.categoryImage,
    },
    productLineNameLink: {
        ...brandDetailsItemSharedStyles.categoryNameLink,
    },
    viewAllButtonItem: {
        width: 12,
        css: css`
            padding-top: 5px;
            width: 100%;
            justify-content: center;
        `,
    },
    viewAllButton: {
        typographyProps: {
            weight: "bold",
            ellipsis: true,
        },
    },
};

const styles = productLinesStyles;

const BrandDetailsProductLines: FC<Props> = ({ fields }) => {
    const { isLoading, value: brandProductLines } = useContext(BrandProductLinesStateContext);
    const { title, showImages, gridType, maxToShow } = fields;
    const productLineItemStyles = styles.productLineItemRow;
    // const productLineItemStyles = gridType === "row" ? styles.productLineItemRow : styles.productLineItemColumn;
    const [viewCount, setViewCount] = React.useState(maxToShow);
    useEffect(() => {
        setViewCount(maxToShow);
    }, [maxToShow]);
    if (isLoading || !brandProductLines || brandProductLines!.length === 0) {
        return null;
    }

    const handleShowAllClicked = () => {
        setViewCount(brandProductLines?.length || maxToShow);
    };
    const renderShowAllLink = () => {
        if (maxToShow === 0 || (brandProductLines || []).length <= viewCount) {
            return null;
        }
        return (
            <GridItem {...styles.viewAllButtonItem}>
                <Button
                    onClick={handleShowAllClicked}
                    {...styles.viewAllButton}
                    data-test-selector="brandProductLineShowAllLink"
                >
                    {translate("Show All")}
                </Button>
            </GridItem>
        );
    };

    // XNG-Changes: Update structure
    return (
        <StyledWrapper {...styles.container} data-test-selector="brandProductLines">
            <Typography {...styles.title} data-test-selector="brandProductLinesTitle">
                {title}
            </Typography>
            <GridContainer {...styles.productLineContainer}>
                {brandProductLines?.slice(0, viewCount).map(productLine => (
                    <GridItem key={productLine.id} {...productLineItemStyles}>
                        <Clickable href={productLine.productListPagePath} {...styles.innerProductLineContainer}>
                            <LazyImage
                                src={productLine.featuredImagePath}
                                altText={productLine.featuredImageAltText}
                                {...styles.productLineImage}
                                data-test-selector={`brandProductLineImage_${productLine.id}`}
                            />
                        </Clickable>
                        <Link
                            href={productLine.productListPagePath}
                            {...styles.productLineNameLink}
                            data-test-selector={`brandProductLineLink_${productLine.id}`}
                        >
                            {productLine.name}
                        </Link>
                    </GridItem>
                ))}
                {renderShowAllLink()}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandDetailsProductLines),
    definition: {
        group: "Brand Details",
        displayName: "Brand Product Lines",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Product Lines",
                fieldType: "Translatable",
            },
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.maxToShow,
                displayName: "Max To Show",
                editorTemplate: "IntegerField",
                defaultValue: 8,
                fieldType: "General",
            },
            {
                name: fields.gridType,
                displayName: "Grid Type",
                editorTemplate: "DropDownField",
                defaultValue: "row",
                fieldType: "General",
                options: [
                    { value: "row", displayName: "Row" },
                    { value: "column", displayName: "Column" },
                ],
                isVisible: item => false,
            },
        ],
    },
};

export default widgetModule;
